import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm, FormToggle, FormTextField } from '@fs/zion-form'
import { Button } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import { PreferenceInputChips, PermissionInputChips } from './ChipFields'
import { DEFAULT_FORM_VALUES } from './formUtils'
import { useFormQuerySync, useFormSubmit } from './useFormQuerySync'
import { parseQueryParams } from './queryParamUtils'

export default function DynamicLinksOverrideForm() {
  const { t } = useTranslation()
  const { query } = useQueryParams()

  // Parse query parameters into form default values
  const defaultValues = useMemo(() => {
    return parseQueryParams(query)
  }, [query])

  const methods = useZionForm({
    mode: 'onChange',
    defaultValues,
  })

  // Set up form query synchronization
  const { syncToURL } = useFormQuerySync({
    onFormUpdate: (values) => {
      // Update form values when URL changes
      Object.entries(values).forEach(([fieldName, value]) => {
        const currentValue = methods.getValues(fieldName)

        // Only update if the value has actually changed and field is not dirty or touched
        const isDirty = methods.formState.dirtyFields[fieldName]
        const isTouched = methods.formState.touchedFields[fieldName]

        if (JSON.stringify(currentValue) !== JSON.stringify(value) && !isDirty && !isTouched) {
          methods.setValue(fieldName, value, {
            shouldDirty: false,
            shouldTouch: false,
          })
        }
      })
    },
    onTouchedUpdate: (touched) => {
      // Mark fields as touched based on URL params
      Object.entries(touched).forEach(([key, isTouched]) => {
        if (isTouched) {
          methods.trigger(key)
        }
      })
    },
    getCurrentValues: () => methods.getValues(),
    getCurrentTouched: () => methods.formState.touchedFields,
  })

  // Set up form submission handlers
  const { handleSubmit: submitHandler } = useFormSubmit({
    getCurrentValues: () => methods.getValues(),
    getCurrentTouched: () => methods.formState.touchedFields,
    onReset: () => {
      methods.reset(DEFAULT_FORM_VALUES)
    },
  })

  // Handle form submission
  const handleSubmit = () => {
    submitHandler()
  }

  // Sync form changes to URL in real-time
  React.useEffect(() => {
    syncToURL()
  }, [methods.formState.touchedFields, syncToURL])

  return (
    <ZionForm methods={methods} onSubmit={handleSubmit}>
      <FormTextField name="forceCountry" label="Country" placeholder="Enter country" />
      <PreferenceInputChips methods={methods} />
      <PermissionInputChips methods={methods} />
      <FormToggle name="forceRussianAccess" label="Russian" />
      <FormToggle name="forceTempleViewable" label="Temple Viewable" />
      <FormToggle name="forceChurchMembership" label="Church Member" />
      <FormToggle name="forceSignedOut" label="Force Signed Out" />
      <Button type="submit" emphasis="high">
        {t('header.settings.submit', 'Submit')}
      </Button>
    </ZionForm>
  )
}
